import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Building2, Sun, Home, Heart, Bath, Stethoscope } from 'lucide-react'
import SEO from '@/components/common/SEO'
import SubpageHero from '@/components/common/SubpageHero'
import PageContainer from '@/components/layout/PageContainer'
import Card from '@/components/ui/Card'
import Button from '@/components/ui/Button'
import { ROUTES } from '@/lib/constants'
import PromotionSlot from '@/components/common/PromotionSlot'

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
}

const FACILITY_TYPES = [
  {
    icon: Building2,
    name: '노인요양시설 (요양원)',
    target: '1~5등급',
    summary: '24시간 전문 돌봄이 필요한 어르신',
    description: '전문 요양보호사가 24시간 상주하며 식사, 목욕, 배설 등 일상생활을 도와드립니다. 의료, 간호, 재활 프로그램도 함께 제공됩니다.',
    services: ['24시간 요양보호', '식사 제공 (3식)', '건강관리 및 간호', '재활 프로그램', '인지활동 프로그램'],
    cost: '월 약 50~180만원 (등급, 감경에 따라 다름)',
    suitable: '거동이 불편하거나 혼자 생활이 어려운 분, 가족 돌봄이 어려운 경우',
    color: 'from-blue-100 to-indigo-100',
    iconColor: 'text-cb-primary',
  },
  {
    icon: Sun,
    name: '주간보호 (데이케어)',
    target: '1~5등급, 인지지원등급',
    summary: '낮 시간에만 돌봄이 필요한 어르신',
    description: '낮 동안 시설에서 돌봄을 받고 저녁에는 집으로 돌아갑니다. 집에서 생활하면서 낮 시간 전문 돌봄을 받을 수 있어 가족 부담을 줄여줍니다.',
    services: ['낮 시간 돌봄 (8~12시간)', '점심/간식 제공', '송영 서비스 (차량)', '인지/신체 프로그램', '목욕 서비스'],
    cost: '월 약 30~80만원',
    suitable: '가족과 함께 살지만 낮에 돌봄이 필요한 분, 사회활동을 원하는 분',
    color: 'from-amber-100 to-orange-100',
    iconColor: 'text-cb-accent',
  },
  {
    icon: Home,
    name: '방문요양',
    target: '1~5등급',
    summary: '집에서 돌봄을 받고 싶은 어르신',
    description: '요양보호사가 어르신의 자택을 방문하여 신체활동, 가사활동, 인지활동 등을 도와드립니다. 익숙한 환경에서 돌봄을 받을 수 있습니다.',
    services: ['신체활동 지원 (식사, 옷 갈아입기 등)', '가사활동 (청소, 빨래, 요리)', '인지활동 프로그램', '외출 동행', '정서 지원'],
    cost: '월 약 20~60만원 (이용시간에 따라)',
    suitable: '집에서 생활하면서 일부 도움이 필요한 분, 시설 입소를 원하지 않는 분',
    color: 'from-green-100 to-emerald-100',
    iconColor: 'text-cb-secondary',
  },
  {
    icon: Heart,
    name: '치매전담시설',
    target: '치매 진단자',
    summary: '치매 어르신을 위한 전문 시설',
    description: '치매 전문 돌봄 프로그램과 안전한 환경을 갖춘 시설입니다. 배회 방지 시스템, 인지재활 프로그램 등 치매에 특화된 서비스를 제공합니다.',
    services: ['치매 전문 돌봄', '인지재활 프로그램', '배회 방지 안전시설', '행동심리증상 관리', '가족 상담'],
    cost: '월 약 50~180만원',
    suitable: '치매 진단을 받은 어르신, 전문적 치매 관리가 필요한 분',
    color: 'from-purple-100 to-pink-100',
    iconColor: 'text-purple-600',
  },
  {
    icon: Bath,
    name: '방문목욕',
    target: '1~5등급',
    summary: '목욕 도움이 필요한 어르신',
    description: '목욕 장비를 갖춘 차량이 자택을 방문하여 목욕 서비스를 제공합니다. 2인 1조로 안전하게 진행됩니다.',
    services: ['이동식 욕조 이용 목욕', '2인 1조 안전 목욕', '주 1~2회 방문'],
    cost: '회당 약 7~8만원 (본인부담 약 1.5만원)',
    suitable: '거동이 불편하여 혼자 목욕이 어려운 분',
    color: 'from-cyan-100 to-sky-100',
    iconColor: 'text-cyan-600',
  },
  {
    icon: Stethoscope,
    name: '방문간호',
    target: '1~5등급',
    summary: '의료적 관리가 필요한 어르신',
    description: '간호사가 자택을 방문하여 건강 상태 확인, 투약 관리, 욕창 관리 등 간호 서비스를 제공합니다.',
    services: ['건강 상태 모니터링', '투약 관리', '욕창 등 상처 관리', '재활 운동 지도', '의사 지시에 따른 간호'],
    cost: '회당 약 4~5만원 (본인부담 약 1만원)',
    suitable: '만성질환 관리가 필요한 분, 의료적 처치가 필요한 분',
    color: 'from-rose-100 to-red-100',
    iconColor: 'text-rose-600',
  },
]

export default function GuideFacilityTypePage() {
  const navigate = useNavigate()

  return (
    <>
      <SEO
        title="요양시설 종류 비교 | 요양원·주간보호·방문요양 차이"
        description="요양원·주간보호·방문요양·치매전담·방문목욕·방문간호 6가지 요양시설 유형을 대상·형태·월 비용으로 비교합니다. 어르신 상태에 맞는 서비스를 찾아보세요."
        url="/guide/facility-types"
      />
      <SubpageHero title="요양시설 종류" description="시설 유형별 특징과 차이점 비교" icon={<Building2 size={20} className="text-white" />} />
      <PageContainer>
        <motion.div
          className="flex flex-col gap-6"
          initial="hidden"
          animate="visible"
          variants={{ visible: { transition: { staggerChildren: 0.05 } } }}
        >
          {/* 소개 */}
          <motion.section variants={fadeUp}>
            <h1 className="text-h2 font-bold text-cb-gray-900">요양시설, 어떤 종류가 있나요?</h1>
            <p className="mt-2 text-body text-cb-gray-600 leading-relaxed">
              장기요양등급을 받으면 다양한 유형의 요양서비스를 이용할 수 있습니다.
              어르신의 상태와 가족 상황에 맞는 서비스를 선택하는 것이 중요합니다.
            </p>
          </motion.section>

          {/* 한눈에 비교 */}
          <motion.section variants={fadeUp}>
            <h2 className="text-h3 font-bold text-cb-gray-900 mb-4">한눈에 비교</h2>
            <Card noPadding className="overflow-x-auto">
              <table className="w-full text-caption min-w-[500px]">
                <thead>
                  <tr className="bg-cb-gray-50">
                    <th className="px-3 py-2.5 text-left font-semibold text-cb-gray-600">유형</th>
                    <th className="px-3 py-2.5 text-left font-semibold text-cb-gray-600">형태</th>
                    <th className="px-3 py-2.5 text-left font-semibold text-cb-gray-600">대상</th>
                    <th className="px-3 py-2.5 text-left font-semibold text-cb-gray-600">월 비용</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t border-cb-gray-100"><td className="px-3 py-2.5 font-medium">요양원</td><td className="px-3 py-2.5">입소 (24시간)</td><td className="px-3 py-2.5">1~5등급</td><td className="px-3 py-2.5">50~180만원</td></tr>
                  <tr className="border-t border-cb-gray-100"><td className="px-3 py-2.5 font-medium">주간보호</td><td className="px-3 py-2.5">통원 (낮)</td><td className="px-3 py-2.5">전 등급</td><td className="px-3 py-2.5">30~80만원</td></tr>
                  <tr className="border-t border-cb-gray-100"><td className="px-3 py-2.5 font-medium">방문요양</td><td className="px-3 py-2.5">자택 방문</td><td className="px-3 py-2.5">1~5등급</td><td className="px-3 py-2.5">20~60만원</td></tr>
                  <tr className="border-t border-cb-gray-100"><td className="px-3 py-2.5 font-medium">치매전담</td><td className="px-3 py-2.5">입소/통원</td><td className="px-3 py-2.5">치매 진단</td><td className="px-3 py-2.5">50~180만원</td></tr>
                  <tr className="border-t border-cb-gray-100"><td className="px-3 py-2.5 font-medium">방문목욕</td><td className="px-3 py-2.5">자택 방문</td><td className="px-3 py-2.5">1~5등급</td><td className="px-3 py-2.5">회당 1.5만원</td></tr>
                  <tr className="border-t border-cb-gray-100"><td className="px-3 py-2.5 font-medium">방문간호</td><td className="px-3 py-2.5">자택 방문</td><td className="px-3 py-2.5">1~5등급</td><td className="px-3 py-2.5">회당 1만원</td></tr>
                </tbody>
              </table>
            </Card>
          </motion.section>

          {/* 시설별 상세 */}
          {FACILITY_TYPES.map((type) => (
            <motion.section key={type.name} variants={fadeUp}>
              <Card>
                <div className="flex items-center gap-3 mb-3">
                  <div className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br ${type.color}`}>
                    <type.icon size={22} className={type.iconColor} />
                  </div>
                  <div>
                    <h3 className="text-body font-bold text-cb-gray-900">{type.name}</h3>
                    <p className="text-caption text-cb-gray-500">대상: {type.target}</p>
                  </div>
                </div>

                <p className="text-body-sm text-cb-gray-700 leading-relaxed mb-3">{type.description}</p>

                <div className="mb-3">
                  <p className="text-caption font-semibold text-cb-gray-600 mb-1.5">주요 서비스</p>
                  <div className="flex flex-wrap gap-1.5">
                    {type.services.map((s) => (
                      <span key={s} className="rounded-md bg-cb-gray-50 px-2 py-0.5 text-[10px] text-cb-gray-600">{s}</span>
                    ))}
                  </div>
                </div>

                <div className="flex flex-col gap-1.5 rounded-xl bg-cb-gray-50 p-3">
                  <div className="flex justify-between text-caption">
                    <span className="text-cb-gray-500">예상 비용</span>
                    <span className="font-semibold text-cb-gray-800">{type.cost}</span>
                  </div>
                  <div className="text-caption text-cb-gray-500">
                    <span className="font-medium">추천 대상:</span> {type.suitable}
                  </div>
                </div>
              </Card>
            </motion.section>
          ))}

          {/* CTA */}
          <motion.section variants={fadeUp} className="flex flex-col gap-3 pb-4">
            <Button variant="primary" size="lg" fullWidth onClick={() => navigate(ROUTES.FACILITIES)}>
              내 근처 요양시설 찾아보기
            </Button>
            <Button variant="ghost" size="lg" fullWidth onClick={() => navigate(ROUTES.COST_SIMULATOR)}>
              비용 계산해보기
            </Button>
          </motion.section>

          {/* 프로모션 슬롯: guide_bottom */}
          <PromotionSlot slotKey="guide_bottom" className="mt-2" />
        </motion.div>
      </PageContainer>
    </>
  )
}
